
import type { ComputedRef } from 'vue'
import type { AppTextareaEvent, MultiselectSearchOption, NativeSelectOption } from '@shared/ui/fields'
import type { CustomerForm } from '@entities/customer'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'
import { timezones } from '@shared/config'

import { getKidUrl } from './list/customer-links'
import { useCustomerPrivileges } from './customer-privileges.hook'

const YES = 'YES'
const NO = 'NO'

/** The legacy select offered only these two providers; PayPal customers keep theirs until changed. */
const PAYMENT_PROVIDERS: Record<string, string> = {
  STRIPE: 'Stripe',
  SOLIDGATE: 'Solidgate',
}

interface CustomerKidLink {
  id: number
  name: string
  url: string
}

interface CustomerFieldsReturn {
  hasForm: ComputedRef<boolean>
  canViewCustomers: ComputedRef<boolean>
  refKids: ComputedRef<CustomerKidLink[]>
  refFirstName: ComputedRef<string>
  refLastName: ComputedRef<string>
  refEmail: ComputedRef<string>
  refPhone: ComputedRef<string>
  refTimezone: ComputedRef<string>
  refNotes: ComputedRef<string>
  refContactChannels: ComputedRef<string[]>
  refReminderChannels: ComputedRef<string[]>
  timezones: string[]
  screenRecordingOptions: ComputedRef<NativeSelectOption[]>
  aiSummarizeOptions: ComputedRef<NativeSelectOption[]>
  pricingModelOptions: ComputedRef<NativeSelectOption[]>
  paymentProviderOptions: ComputedRef<NativeSelectOption[]>
  contactChannelOptions: ComputedRef<MultiselectSearchOption[]>
  reminderChannelOptions: ComputedRef<MultiselectSearchOption[]>
  setText: (key: 'first_name' | 'last_name' | 'email' | 'phone', value: string | number) => void
  setTimezone: (value: string) => void
  setScreenRecording: (option: NativeSelectOption) => void
  setAiSummarize: (option: NativeSelectOption) => void
  setPricingModel: (option: NativeSelectOption) => void
  setPaymentProvider: (option: NativeSelectOption) => void
  setContactChannels: (selected: MultiselectSearchOption[]) => void
  setReminderChannels: (selected: MultiselectSearchOption[]) => void
  setNotes: (event: AppTextareaEvent) => void
}

const toSelectOptions = (source: Record<string, string>, selected: string | null): NativeSelectOption[] =>
  Object.entries(source).map(([value, text]) => ({
    id: value,
    value,
    text,
    selected: value === selected,
  }))

const toYesNoOptions = (value: boolean): NativeSelectOption[] =>
  toSelectOptions({ [YES]: 'Yes', [NO]: 'No' }, value ? YES : NO)

const toMultiOptions = (source: Record<string, string>, selected: string[]): MultiselectSearchOption[] =>
  Object.entries(source).map(([value, text]) => ({
    id: value,
    value,
    text,
    selected: selected.includes(value),
  }))

const toValues = (selected: MultiselectSearchOption[]): string[] => selected.map(option => String(option.value))

/**
 * The General info form. With view-customers it edits the `customers.details` draft; without it the
 * rows the legacy page still showed (kids, names, timezone) come read-only from `customers.show`.
 */
export const useCustomerFields = (): CustomerFieldsReturn => {
  const customerStore = useCustomerStore()
  const listsStore = useListsStore()
  const { canViewCustomers } = useCustomerPrivileges()

  const form = computed<CustomerForm | null>(() => customerStore.getForm)
  const hasForm = computed<boolean>(() => !!form.value)

  const refKids = computed<CustomerKidLink[]>(() => {
    const kids = customerStore.getDetails?.kids ?? customerStore.getCustomer?.kids ?? []

    return kids.map(kid => ({
      id: kid.id,
      name: kid.name,
      url: getKidUrl(kid.id),
    }))
  })

  const refFirstName = computed<string>(() => form.value?.first_name ?? customerStore.getCustomer?.first_name ?? '')
  const refLastName = computed<string>(() => form.value?.last_name ?? customerStore.getCustomer?.last_name ?? '')
  const refEmail = computed<string>(() => form.value?.email ?? '')
  const refPhone = computed<string>(() => form.value?.phone ?? '')
  const refTimezone = computed<string>(() => form.value?.timezone ?? customerStore.getCustomer?.timezone ?? '')
  const refNotes = computed<string>(() => form.value?.notes ?? '')
  const refContactChannels = computed<string[]>(() => form.value?.contact_data.contact_channels ?? [])
  const refReminderChannels = computed<string[]>(() => form.value?.contact_data.notification_channels ?? [])

  const screenRecordingOptions = computed<NativeSelectOption[]>(() => toYesNoOptions(!!form.value?.screen_recording))
  const aiSummarizeOptions = computed<NativeSelectOption[]>(() => toYesNoOptions(!!form.value?.ai_summarize))
  const pricingModelOptions = computed<NativeSelectOption[]>(() =>
    toSelectOptions(listsStore.getLists?.customer?.pricing_model ?? {}, form.value?.pricing_model ?? null))
  const paymentProviderOptions = computed<NativeSelectOption[]>(() =>
    toSelectOptions(PAYMENT_PROVIDERS, form.value?.payment_provider || null))
  const contactChannelOptions = computed<MultiselectSearchOption[]>(() =>
    toMultiOptions(listsStore.getLists?.customer?.channel?.contact ?? {}, refContactChannels.value))
  const reminderChannelOptions = computed<MultiselectSearchOption[]>(() =>
    toMultiOptions(listsStore.getLists?.customer?.channel?.all ?? {}, refReminderChannels.value))

  const setText = (key: 'first_name' | 'last_name' | 'email' | 'phone', value: string | number): void => {
    customerStore.setFormField(key, String(value))
  }

  const setTimezone = (value: string): void => {
    customerStore.setFormField('timezone', value)
  }

  const setScreenRecording = (option: NativeSelectOption): void => {
    customerStore.setFormField('screen_recording', option.value === YES)
  }

  const setAiSummarize = (option: NativeSelectOption): void => {
    customerStore.setFormField('ai_summarize', option.value === YES)
  }

  const setPricingModel = (option: NativeSelectOption): void => {
    customerStore.setFormField('pricing_model', String(option.value ?? ''))
  }

  const setPaymentProvider = (option: NativeSelectOption): void => {
    customerStore.setFormField('payment_provider', String(option.value ?? ''))
  }

  const setContactChannels = (selected: MultiselectSearchOption[]): void => {
    if (!form.value) return

    customerStore.setFormField('contact_data', { ...form.value.contact_data, contact_channels: toValues(selected) })
  }

  const setReminderChannels = (selected: MultiselectSearchOption[]): void => {
    if (!form.value) return

    customerStore.setFormField('contact_data', { ...form.value.contact_data, notification_channels: toValues(selected) })
  }

  const setNotes = (event: AppTextareaEvent): void => {
    customerStore.setFormField('notes', event.value)
  }

  return {
    hasForm,
    canViewCustomers,
    refKids,
    refFirstName,
    refLastName,
    refEmail,
    refPhone,
    refTimezone,
    refNotes,
    refContactChannels,
    refReminderChannels,
    timezones,
    screenRecordingOptions,
    aiSummarizeOptions,
    pricingModelOptions,
    paymentProviderOptions,
    contactChannelOptions,
    reminderChannelOptions,
    setText,
    setTimezone,
    setScreenRecording,
    setAiSummarize,
    setPricingModel,
    setPaymentProvider,
    setContactChannels,
    setReminderChannels,
    setNotes,
  }
}
